import { useState, useMemo, useEffect, useContext } from 'react';
import {
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    ToggleButton,
    ToggleButtonGroup,
} from '@mui/material';
import CustomPlayerImageWithDefault from '@/components/shared/custom/CustomPlayerImageWithDefault';
import { formatPlayerName } from '@/helpers/players-helper';
import { toPriceInEuros } from '@/helpers/prices-helper';
import FantasyLaLigaIcon from '@/components/shared/icons/fantasy-la-liga-icon';
import BiwengerIcon from '@/components/shared/icons/biwenger-icon';
import FantasyMarcaIcon from '@/components/shared/icons/FantasyMarcaIcon';
import { Context } from '@/context';
import { REDUCERS } from '@/constants/reducers';
import { FANTASY } from '@/helpers/biwenger-helper';
import MisterFantasyIcon from '@/components/shared/icons/MisterFantasyIcon';

const RankingModal = ({ open, onClose, title, jugadores, config }) => {
    const getValueColor = (value) => {
        if (!config.shouldDisplayColor) return 'inherit';
        if (value > 0) {
            return '#206945';
        }
        if (value < 0) {
            return '#963c3c';
        }
        return 'inherit';
    };

    const getTextColor = (value) => {
        if (!config.shouldDisplayColor) return 'inherit';
        if (value > 0) {
            return '#fff';
        }
        if (value < 0) {
            return '#fff';
        }
        return 'inherit';
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>{title}</DialogTitle>
            <DialogContent>
                <TableContainer component={Paper}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 'bold', fontSize: 12 }}>Posición</TableCell>
                                <TableCell sx={{ fontWeight: 'bold', fontSize: 12 }}>Jugador</TableCell>
                                <TableCell sx={{ fontWeight: 'bold', fontSize: 12, textAlign: 'center' }}>
                                    {config.label}
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {jugadores.map((jugador, index) => {
                                const value = config.getValue(jugador);
                                return (
                                    <TableRow key={jugador.id}>
                                        <TableCell sx={{ fontSize: 12, fontWeight: 'bold' }}>
                                            #{index + 1}
                                        </TableCell>
                                        <TableCell sx={{ fontSize: 12 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <CustomPlayerImageWithDefault
                                                    playerId={jugador.id}
                                                    width={24}
                                                    height={24}
                                                />
                                                <Typography component="span" sx={{ fontSize: 12 }}>
                                                    {jugador.name}
                                                </Typography>
                                            </Box>
                                        </TableCell>
                                        <TableCell
                                            sx={{
                                                fontSize: 12,
                                                textAlign: 'center',
                                                background: getValueColor(value),
                                                color: config.shouldDisplayColor
                                                    ? getTextColor(value)
                                                    : 'text.primary',
                                                px: config.shouldDisplayColor ? 0.5 : 0,
                                            }}
                                        >
                                            {config.format(value)}
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cerrar</Button>
            </DialogActions>
        </Dialog>
    );
};

const RankingSection = ({ title, jugadores, config }) => {
    const [modalOpen, setModalOpen] = useState(false);
    const top5Jugadores = jugadores.slice(0, 5);

    const getValueColor = (value) => {
        if (!config.shouldDisplayColor) return 'inherit';
        if (value > 0) {
            return '#206945';
        }
        if (value < 0) {
            return '#963c3c';
        }
        return 'inherit';
    };

    const getTextColor = (value) => {
        if (value > 0) {
            return '#fff';
        }
        if (value < 0) {
            return '#fff';
        }
    };

    return (
        <Box sx={{ mb: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography
                    variant="h6"
                    sx={{ fontSize: 14, display: 'flex', alignItems: 'center', gap: 0.5 }}
                >
                    {config.icon}
                    {title}
                </Typography>
                <Button size="small" variant="text" onClick={() => setModalOpen(true)} sx={{ fontSize: 12 }}>
                    Ver más
                </Button>
            </Box>

            <Box
                sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    border: '1px solid',
                    borderColor: 'border.main',
                    borderRadius: 1,
                    py: 1,
                }}
            >
                {top5Jugadores.map((jugador, index) => {
                    const value = config.getValue(jugador);
                    return (
                        <Box
                            key={jugador.id}
                            sx={{
                                width: '20%',
                                minWidth: '20%',
                                borderRight: index !== top5Jugadores.length - 1 ? '1px solid' : 'none',
                                borderColor: 'border.main',
                            }}
                        >
                            <Box sx={{ textAlign: 'center' }}>
                                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
                                    <CustomPlayerImageWithDefault
                                        playerId={jugador.id}
                                        width={40}
                                        height={40}
                                    />
                                </Box>
                                <Typography
                                    sx={{
                                        fontSize: 12,
                                        fontWeight: 'medium',
                                        lineHeight: 1.2,
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                        px: '5%',
                                    }}
                                >
                                    {formatPlayerName(jugador.name)}
                                </Typography>
                                <Typography
                                    variant="caption"
                                    sx={{
                                        fontSize: 10,
                                        background: getValueColor(value),
                                        color: config.shouldDisplayColor
                                            ? getTextColor(value)
                                            : 'text.primary',
                                        borderRadius: 1,
                                        px: config.shouldDisplayColor ? 0.5 : 0,
                                    }}
                                >
                                    {config.format(value)}
                                </Typography>
                            </Box>
                        </Box>
                    );
                })}
            </Box>

            <RankingModal
                open={modalOpen}
                onClose={() => setModalOpen(false)}
                title={title}
                jugadores={jugadores}
                config={config}
            />
        </Box>
    );
};

const LideresFantasyPlayers = ({ players }) => {
    const jugadores = players ?? [];
    const { state, dispatch } = useContext(Context);
    const fantasy = state.preferences.fantasy;
    const [selectedMode, setSelectedMode] = useState(fantasy);

    useEffect(() => {
        if (fantasy === FANTASY.ANALITICA) {
            setSelectedMode(FANTASY.FANTASY);
        } else {
            setSelectedMode(fantasy);
        }
    }, [fantasy]);

    // Guardar el modo seleccionado en localStorage cuando cambie
    const handleModeChange = (event, newMode) => {
        if (newMode !== null) {
            setSelectedMode(newMode);

            switch (newMode) {
                case FANTASY.FANTASY:
                    dispatch({ type: REDUCERS.UPDATE_PREFERENCES, payload: { fantasy: FANTASY.FANTASY } });
                    break;
                case FANTASY.BIWENGER:
                    dispatch({ type: REDUCERS.UPDATE_PREFERENCES, payload: { fantasy: FANTASY.BIWENGER } });
                    break;
                case FANTASY.FANTASY_MARCA:
                    dispatch({
                        type: REDUCERS.UPDATE_PREFERENCES,
                        payload: { fantasy: FANTASY.FANTASY_MARCA },
                    });
                    break;
                case FANTASY.MISTER_FANTASY:
                    dispatch({
                        type: REDUCERS.UPDATE_PREFERENCES,
                        payload: { fantasy: FANTASY.MISTER_FANTASY },
                    });
                    break;
                default:
                    break;
            }
        }
    };

    const statsConfig = {
        laLigaFantasyPoints: {
            label: 'Puntos La Liga Fantasy',
            getValue: (jugador) => jugador.laLigaFantasyPoints || 0,
            format: (value) => value.toString(),
            sortDesc: true,
            shouldDisplayColor: false,
            icon: <FantasyLaLigaIcon iconWidth={16} iconHeight={16} />,
            mode: FANTASY.FANTASY,
        },
        laLigaFantasyMarketValue: {
            label: 'Valor de Mercado La Liga Fantasy',
            getValue: (jugador) => jugador.laLigaFantasyMarketValue || 0,
            format: (value) => `${toPriceInEuros(value)}`,
            sortDesc: true,
            shouldDisplayColor: false,
            icon: <FantasyLaLigaIcon iconWidth={16} iconHeight={16} />,
            mode: FANTASY.FANTASY,
        },
        laLigaFantasyAverage: {
            label: 'Promedio La Liga Fantasy',
            getValue: (jugador) => jugador.laLigaFantasyAverage || 0,
            format: (value) => value.toFixed(2),
            sortDesc: true,
            shouldDisplayColor: false,
            icon: <FantasyLaLigaIcon iconWidth={16} iconHeight={16} />,
            mode: FANTASY.FANTASY,
        },
        laLigaFantasySubidaDePrecio: {
            label: 'Subida de Precio La Liga Fantasy',
            getValue: (jugador) => jugador.laLigaFantasySubida || 0,
            format: (value) => `${toPriceInEuros(value)}`,
            sortDesc: true,
            shouldDisplayColor: true,
            icon: <FantasyLaLigaIcon iconWidth={16} iconHeight={16} />,
            mode: FANTASY.FANTASY,
        },
        biwengerMarketValue: {
            label: 'Valor de Mercado Biwenger',
            getValue: (jugador) => jugador.biwengerMarketValue || 0,
            format: (value) => `${toPriceInEuros(value)}`,
            sortDesc: true,
            shouldDisplayColor: false,
            icon: <BiwengerIcon width={16} height={16} />,
            mode: FANTASY.BIWENGER,
        },
        biwengerSubida: {
            label: 'Subida de Precio Biwenger',
            getValue: (jugador) => jugador.biwengerSubida || 0,
            format: (value) => `${toPriceInEuros(value)}`,
            sortDesc: true,
            shouldDisplayColor: true,
            icon: <BiwengerIcon width={16} height={16} />,
            mode: FANTASY.BIWENGER,
        },
        biwengerAsPoints: {
            label: 'Puntos Diario As',
            getValue: (jugador) => jugador.biwengerAsPoints || 0,
            format: (value) => value.toString(),
            sortDesc: true,
            shouldDisplayColor: false,
            icon: <BiwengerIcon width={16} height={16} />,
            mode: FANTASY.BIWENGER,
        },
        biwengerSofascorePoints: {
            label: 'Puntos Sofascore',
            getValue: (jugador) => jugador.biwengerSofascorePoints || 0,
            format: (value) => value.toString(),
            sortDesc: true,
            shouldDisplayColor: false,
            icon: <BiwengerIcon width={16} height={16} />,
            mode: FANTASY.BIWENGER,
        },
        biwengerStatsPoints: {
            label: 'Puntos Stats',
            getValue: (jugador) => jugador.biwengerStatsPoints || 0,
            format: (value) => value.toString(),
            sortDesc: true,
            shouldDisplayColor: false,
            icon: <BiwengerIcon width={16} height={16} />,
            mode: FANTASY.BIWENGER,
        },
        biwengerAveragePoints: {
            label: 'Promedio Average',
            getValue: (jugador) => jugador.biwengerAveragePoints || 0,
            format: (value) => value.toFixed(2),
            sortDesc: true,
            shouldDisplayColor: false,
            icon: <BiwengerIcon width={16} height={16} />,
            mode: FANTASY.BIWENGER,
        },
        biwengerSocialPoints: {
            label: 'Puntos Social',
            getValue: (jugador) => jugador.biwengerSocialPoints || 0,
            format: (value) => value.toString(),
            sortDesc: true,
            shouldDisplayColor: false,
            icon: <BiwengerIcon width={16} height={16} />,
            mode: FANTASY.BIWENGER,
        },
        marcaMarketValue: {
            label: 'Valor de Mercado Fantasy Marca',
            getValue: (jugador) => jugador.fantasyMarcaMarketValue || 0,
            format: (value) => `${toPriceInEuros(value)}`,
            sortDesc: true,
            shouldDisplayColor: false,
            icon: <FantasyMarcaIcon width={16} height={16} />,
            mode: FANTASY.FANTASY_MARCA,
        },
        marcaSubida: {
            label: 'Subida de Precio Fantasy Marca',
            getValue: (jugador) => jugador.fantasyMarcaSubida || 0,
            format: (value) => `${toPriceInEuros(value)}`,
            sortDesc: true,
            shouldDisplayColor: true,
            icon: <FantasyMarcaIcon width={16} height={16} />,
            mode: FANTASY.FANTASY_MARCA,
        },
        misterFantasyMarketValue: {
            label: 'Valor de Mercado Mister Fantasy',
            getValue: (jugador) => jugador.misterFantasyMarketValue || 0,
            format: (value) => `${toPriceInEuros(value)}`,
            icon: <MisterFantasyIcon width={16} height={16} />,
            sortDesc: true,
            shouldDisplayColor: false,
            mode: FANTASY.MISTER_FANTASY,
        },
        misterFantasySubida: {
            label: 'Subida de Precio Mister Fantasy',
            getValue: (jugador) => jugador.misterFantasySubida || 0,
            format: (value) => `${toPriceInEuros(value)}`,
            icon: <MisterFantasyIcon width={16} height={16} />,
            sortDesc: true,
            shouldDisplayColor: true,
            mode: FANTASY.MISTER_FANTASY,
        },
    };

    const atLeastOnePlayerHasData = useMemo(() => {
        return Object.entries(statsConfig).some(([key, config]) => {
            return jugadores.some((jugador) => config.getValue(jugador) > 0);
        });
    }, [jugadores]);

    const sortedJugadores = useMemo(() => {
        if (!jugadores || jugadores.length === 0) return {};

        const result = {};
        Object.entries(statsConfig).forEach(([key, config]) => {
            result[key] = [...jugadores].sort((a, b) => {
                const aValue = config.getValue(a);
                const bValue = config.getValue(b);

                if (config.sortDesc) {
                    return bValue - aValue;
                }
                return aValue - bValue;
            });
        });

        return result;
    }, [jugadores]);

    // Filtrar configuraciones según el modo seleccionado y que tengan datos
    const filteredStatsConfig = useMemo(() => {
        const modeFiltered = Object.entries(statsConfig).filter(
            ([key, config]) => config.mode === selectedMode
        );

        // Filtrar solo las configuraciones que tienen datos mayores que 0
        const withData = modeFiltered.filter(([key, config]) => {
            const hasData = jugadores.some((jugador) => {
                const value = config.getValue(jugador);
                return value > 0;
            });
            return hasData;
        });

        return Object.fromEntries(withData);
    }, [selectedMode, jugadores]);

    if (!jugadores || jugadores.length === 0 || !atLeastOnePlayerHasData) return null;

    return (
        <Box mt={2}>
            <Box sx={{ mb: 1 }}>
                <ToggleButtonGroup
                    value={selectedMode}
                    exclusive
                    onChange={handleModeChange}
                    size="small"
                    sx={{
                        '& .MuiToggleButton-root': {
                            border: '1px solid',
                            borderColor: 'border.main',
                            px: 2,
                            '&.Mui-selected': {
                                backgroundColor: 'background.darkCard',

                                '&:hover': {
                                    backgroundColor: 'primary.dark',
                                },
                            },
                            height: '30px',
                            maxHeight: '30px',
                        },
                    }}
                >
                    <ToggleButton value={FANTASY.FANTASY}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <FantasyLaLigaIcon iconWidth={16} iconHeight={16} />
                        </Box>
                    </ToggleButton>
                    <ToggleButton value={FANTASY.BIWENGER}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <BiwengerIcon width={16} height={16} />
                        </Box>
                    </ToggleButton>
                    <ToggleButton value={FANTASY.FANTASY_MARCA}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <FantasyMarcaIcon width={16} height={16} />
                        </Box>
                    </ToggleButton>
                    <ToggleButton value={FANTASY.MISTER_FANTASY}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <MisterFantasyIcon width={16} height={16} />
                        </Box>
                    </ToggleButton>
                </ToggleButtonGroup>
            </Box>

            {/* Secciones de ranking filtradas */}
            {Object.entries(filteredStatsConfig).map(([key, config]) => (
                <RankingSection
                    key={key}
                    title={config.label}
                    jugadores={sortedJugadores[key] || []}
                    config={config}
                    totalPartidos={0}
                    totalMinutos={0}
                />
            ))}
        </Box>
    );
};

export default LideresFantasyPlayers;
